import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { appConfig, lang } from '@shared/services';

const TERMS = [
    {
        title: '1. Disclaimer of Warranties & Limitation of Liability',
        paragraphs: [
            '1.1. The Service is provided "as is" without any express or implied warranties, including but not limited to merchantability, fitness for a particular purpose, and non-infringement.',
            '1.2. We shall not be liable for:',
            'Any direct, indirect, incidental, or consequential damages arising from the use of the Service.',
            'Inaccuracies, errors, or interruptions in third-party websites accessed through the Service.',
            'The content of external resources, including malware, illegal material, or damages caused by their use.',
            '1.3. If you disagree with these terms, your sole remedy is to discontinue using the Service.',
        ],
    },
    {
        title: '2. How the Proxy Service Works',
        paragraphs: [
            '2.1. The Service acts as an intermediary between you and requested resources:',
            'In a direct connection, you communicate with the target server directly.',
            'When using {PROXYNAME}, requests are routed through our servers, and the target site does not see your IP address.',
            '2.2. We do not guarantee complete anonymity because:',
            'Some resources (e.g., images, scripts) may load directly.',
            'Delivered content may differ from the original (e.g., due to caching or modifications).',
        ],
    },
    {
        title: '3. Lawful Use Only',
        paragraphs: [
            '3.1. The Service may only be used for legal purposes. Prohibited activities include:',
            'Distributing illegal content (piracy, malware, etc.).',
            'Violating copyrights or intellectual property rights.',
            'Spamming, phishing, or participating in DDoS attacks.',
            'Actions violating laws in your jurisdiction or the target website\u2019s jurisdiction.',
            '3.2. We reserve the right to block users for abuse (e.g., automated scraping, hotlinking, excessive server load).',
        ],
    },
    {
        title: '4. Trademark',
        paragraphs: [
            '4.1. The {PROXYNAME} name and logo are our intellectual property. Unauthorized use is prohibited.',
        ],
    },
    {
        title: '5. Changes to Terms',
        paragraphs: [
            '5.1. We may modify these terms at any time without prior notice. The current version will always be available on this site.',
        ],
    },
];

function TermsModal({ onClose, t }) {
    const closeButtonRef = useRef(null);
    const translateTerm = (key) => lang.t(key, { PROXYNAME: appConfig.defines.SITE_NAME });

    useEffect(() => {
        document.body.classList.add('modal-open');
        closeButtonRef.current?.focus();

        const closeOnEscape = (event) => {
            if (event.key === 'Escape') onClose();
        };

        document.addEventListener('keydown', closeOnEscape);

        return () => {
            document.body.classList.remove('modal-open');
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [onClose]);

    const closeOnBackdrop = (event) => {
        if (event.target === event.currentTarget) onClose();
    };

    return (
        <div className="modal-backdrop" role="presentation" onMouseDown={closeOnBackdrop}>
            <section className="terms-modal" role="dialog" aria-modal="true" aria-labelledby="version2-terms-title">
                <button ref={closeButtonRef} className="modal-close" type="button" onClick={onClose} aria-label={t('termsClose')}>
                    <span />
                    <span />
                </button>

                <h2 id="version2-terms-title">{translateTerm('Terms of Service')}</h2>
                <p className="terms-intro">
                    {translateTerm('By using this website and our proxy servers (hereinafter referred to as "{PROXYNAME}", the "Service", "we", "us"), you agree to the following terms:')}
                </p>

                <div className="terms-copy">
                    {TERMS.map((term) => (
                        <section key={term.title}>
                            <h3>{translateTerm(term.title)}</h3>
                            {term.paragraphs.map((paragraph) => (
                                <p key={paragraph}>{translateTerm(paragraph)}</p>
                            ))}
                        </section>
                    ))}
                </div>
            </section>
        </div>
    );
}

TermsModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

export default TermsModal;
